import api, { API_BASE_URL } from '../api';
import React, { useContext, useEffect, useReducer } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate } from 'react-router-dom';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Card from 'react-bootstrap/Card';
import Button from 'react-bootstrap/Button';
import ListGroup from 'react-bootstrap/ListGroup';
import { Store } from '../Store';
import CheckoutSteps from '../components/CheckoutSteps';
import LoadingBox from '../components/LoadingBox';
import { toast } from 'react-toastify';
import { getError } from '../Util';
import CardHeader from '../components/CardHeadder';
import { Container, Spinner } from 'react-bootstrap';
import jsPDF from 'jspdf';
import WhatsAppInstructions from '../components/WhatsAppInstructions';
import toplogo from '../assets/top_logo.png'; // Adjust the path to your logo image

const reducer = (state, action) => {
  switch (action.type) {
    case 'CREATE_REQUEST':
      return { ...state, loading: true };
    case 'CREATE_SUCCESS':
      return { ...state, loading: false };
    case 'CREATE_FAIL':
      return { ...state, loading: false };
    default:
      return state;
  }
};

export default function PlaceOrderScreen() {
  const navigate = useNavigate();

  const [{ loading }, dispatch] = useReducer(reducer, {
    loading: false,
  });

  const { state, dispatch: ctxDispatch } = useContext(Store);
  const { cart } = state;

  const round2 = (num) => Math.round(num * 100 + Number.EPSILON) / 100;
  cart.itemsPrice = round2(0);
  cart.shippingPrice = round2(0);
  cart.taxPrice = round2(0);
  cart.totalPrice = round2(0);

  const [showInstructions, setShowInstructions] = React.useState(false);
  const [pdfFileName, setPdfFileName] = React.useState('');

  const [orderWhatsapp, setOrderWhatsapp] = React.useState('94703533536');

  React.useEffect(() => {
    const fetchSettings = async () => {
      try {
        const { data } = await api.get('/api/settings');
        if (data && data.order_whatsapp_number) {
          setOrderWhatsapp(data.order_whatsapp_number.replace(/\+/g, ''));
        }
      } catch (err) {
        console.error('Error fetching settings', err);
      }
    };
    fetchSettings();
  }, []);

  const placeOrderHandler = async () => {
    try {
      dispatch({ type: 'CREATE_REQUEST' });
      const { data } = await api.post('/api/orders', {
        orderItems: cart.cartItems.map((x) => ({
          ...x,
          product: x._id, // Map for backend compatibility
        })),
        shippingAddress: cart.shippingAddress,
        itemsPrice: cart.itemsPrice,
        shippingPrice: cart.shippingPrice,
        taxPrice: cart.taxPrice,
        totalPrice: cart.totalPrice,
      });

      // Generate PDF
      const doc = new jsPDF();
      let yPos = 15;
      const pageWidth = doc.internal.pageSize.width;
      const margin = 15;

      // Logo - circular shape (equal width and height)
      doc.addImage(toplogo, 'PNG', margin, yPos, 20, 20);
      
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(22);
      doc.setTextColor(128, 16, 1); // customRed (#801001)
      doc.text('Kobithan Garments', margin + 25, yPos + 10);
      
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(0);
      const companyAddress = doc.splitTextToSize(
        'No.14, Eight Acre, Viyasar Road, Thonikkal, Vavuniya, Sri Lanka. 43000\n' +
          'Tel: +94 703533536\n' +
          'Email: kohila@kobithangarments.com',
        120
      );
      doc.text(companyAddress, margin + 25, yPos + 18);

      yPos += 40; // Move down below the header

      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.text(`Order Summary - #${data.order.orderCode}`, margin, yPos);
      doc.setFontSize(10);
      doc.text(
        `Date: ${new Date(data.order.createdAt).toLocaleDateString()}`,
        pageWidth - margin,
        yPos,
        { align: 'right' }
      );
      yPos += 12;

      doc.setDrawColor(200);
      doc.line(margin, yPos, pageWidth - margin, yPos);
      yPos += 10;

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(12);
      doc.text('Customer Details:', margin, yPos);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      doc.text(
        `${cart.shippingAddress.fullName}\n` +
          `WhatsApp: ${cart.shippingAddress.whatsapp}\n` +
          `${cart.shippingAddress.address}\n` +
          `${cart.shippingAddress.city}`,
        margin,
        yPos + 8
      );

      const orderX = pageWidth - margin - 60;
      doc.setFont('helvetica', 'bold');
      doc.text('Order Details:', orderX, yPos);
      doc.setFont('helvetica', 'normal');
      doc.text(`Order ID: ${data.order.orderCode}`, orderX, yPos + 6);
      yPos += 35;

      // Table Header - without Price and Total columns
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(11);
      doc.text('#', margin, yPos);
      doc.text('Description', margin + 10, yPos);
      doc.text('Size', margin + 90, yPos);
      
      const hasColor = cart.cartItems.some(item => item.selectedColor);
      if (hasColor) {
        doc.text('Color', margin + 115, yPos);
      }
      
      doc.text('Qty', hasColor ? margin + 145 : margin + 120, yPos);
      yPos += 6;
      doc.setDrawColor(0);
      doc.line(margin, yPos, pageWidth - margin, yPos);
      yPos += 8;

      // Table Body - without Price and Total
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      cart.cartItems.forEach((item, index) => {
        doc.text(`${index + 1}.`, margin, yPos);
        doc.text(`${item.name} (${item.code})`, margin + 10, yPos, { maxWidth: 75 });
        doc.text(item.selectedSize || 'N/A', margin + 90, yPos);
        if (hasColor) {
          doc.text(item.selectedColor || '-', margin + 115, yPos);
        }
        doc.text(`${item.quantity}`, hasColor ? margin + 145 : margin + 120, yPos);
        yPos += 10;
      });

      yPos += 5;
      doc.line(margin, yPos, pageWidth - margin, yPos);

      doc.setFontSize(9);
      doc.setFont('helvetica', 'italic');
      doc.setTextColor(100);
      yPos = doc.internal.pageSize.height - 15;
      doc.text('Thank you for your order!', margin, yPos);
      doc.text(
        'Terms: Goods sold are not returnable',
        pageWidth - margin,
        yPos,
        { align: 'right' }
      );

      const fileName = `order_${data.order.orderCode}.pdf`;
      setPdfFileName(fileName);

      // PDF is generated and can be downloaded from order details page
      // No automatic upload - order link is shared via WhatsApp instead

      const shopNumber = orderWhatsapp;
      const itemsList = cart.cartItems
        .map(
          (item) =>
            `- ${item.name} - ${item.selectedSize || 'N/A'}${item.selectedColor ? `, ${item.selectedColor}` : ''} (Qty: ${
              item.quantity
            })`
        )
        .join('\n');

      // Create order page URL for reference
      const orderPageUrl = `${window.location.origin}/order/${data.order.orderCode}`;

      const message =
        `*NEW ORDER RECEIVED*\n\n` +
        `*Order ID:* ${data.order.orderCode}\n` +
        `*Customer:* ${cart.shippingAddress.fullName}\n` +
        `*WhatsApp:* ${cart.shippingAddress.whatsapp}\n\n` +
        `*Items:*\n${itemsList}\n\n` +
        `*View Order:* ${orderPageUrl}`;

      const whatsappUrl = `https://wa.me/${shopNumber}?text=${encodeURIComponent(
        message
      )}`;
      window.open(whatsappUrl, '_blank');

      setShowInstructions(true);
      ctxDispatch({ type: 'CART_CLEAR' });
      dispatch({ type: 'CREATE_SUCCESS' });
      localStorage.removeItem('cartItems');
      navigate(`/order/${data.order.orderCode}`);
    } catch (err) {
      dispatch({ type: 'CREATE_FAIL' });
      toast.error(getError(err));
    }
  };

  useEffect(() => {
    if (!cart.shippingAddress.address) {
      navigate('/shipping');
    }
  }, [cart, navigate]);

  return (
    <div>
      <WhatsAppInstructions
        show={showInstructions}
        onClose={() => setShowInstructions(false)}
        fileName={pdfFileName}
      />
      <div>
        <CardHeader />
        <Container fluid="md" className="mt-5 py-5">
          <div className="marginAll">
            <CheckoutSteps step1 step2 step3></CheckoutSteps>
            <Helmet>
              <title>Preview Order</title>
            </Helmet>
            <br />
            <h1 style={{ fontSize: '2rem', color: '#801001', fontWeight: 'bold' }}>
              My Order Summary
            </h1>
            <br />

            <Row>
              <Col md={8}>
                <Row>
                  <Col md={6} className="d-flex">
                    <Card className="mb-3 mt-5 bg-light text-dark w-100 h-90">
                      <Card.Body>
                        <Card.Title style={{ color: '#801001' }}>
                          Shipping
                        </Card.Title>
                        <Card.Text>
                          <strong>Name:-</strong>{' '}
                          {cart.shippingAddress.fullName} <br />
                          <strong>Address:- </strong>
                          {cart.shippingAddress.address},{' '}
                          {cart.shippingAddress.city},{' '}
                          {cart.shippingAddress.postalCode},{' '}
                          {cart.shippingAddress.country}
                          <br />
                        </Card.Text>
                        <Card.Text>
                          <strong>Whatsapp Number:-</strong>{' '}
                          {cart.shippingAddress.whatsapp} <br />
                          <strong>Email Address:- </strong>{' '}
                          {cart.shippingAddress.email}
                          <br />
                        </Card.Text>
                        <Link to="/shipping" className="custom-btn">
                          Edit Details
                        </Link>
                      </Card.Body>
                    </Card>
                  </Col>

                </Row>
              </Col>
            </Row>

            <Col>
              <Card className="mb-3 bg-light text-dark">
                <Card.Body>
                  <Card.Title style={{ color: '#801001' }}>Items</Card.Title>

                  <ListGroup variant="flush">
                    {cart.cartItems.map((item) => (
                      <ListGroup.Item key={item._id}>
                        <Row className="align-items-center">
                          {/* Image and Name */}
                          <Col
                            md={5}
                            className="d-flex align-items-center gap-3"
                          >
                            <img
                              src={item.image ? (item.image.startsWith('http') ? item.image : `${API_BASE_URL}/storage/${item.image}`) : ''}
                              alt={item.name}
                              className="img-fluid rounded img-thumbnail"
                              style={{
                                width: '100px',
                                height: '100px',
                                objectFit: 'cover',
                              }}
                            />
                            <Link
                              className="cardname"
                              to={`/product/${item.slug}`}
                            >
                              <strong style={{ color: '#801001' }}>
                                {item.name}
                              </strong>
                            </Link>
                          </Col>

                          {/* ID, Size, Quantity Controls, and Delete Button */}
                          <Col
                            md={7}
                            className="d-flex align-items-center justify-content-between flex-wrap gap-2"
                          >
                            <strong style={{ color: '#801001' }}>
                              ID: {item.code}
                            </strong>
                            {item.selectedSize && (
                              <strong style={{ color: '#801001' }}>
                                Size: {item.selectedSize}
                              </strong>
                            )}
                            {item.selectedColor && (
                              <strong style={{ color: '#801001' }}>
                                Color: {item.selectedColor}
                              </strong>
                            )}

                            {/* Quantity Controls */}
                            <strong style={{ color: '#801001' }}>
                              Qty: {item.quantity}
                            </strong>
                          </Col>
                        </Row>
                      </ListGroup.Item>
                    ))}
                  </ListGroup>

                  <div className="d-flex justify-content-between align-items-center mt-3">
                    <Link className="custom-btn" to="/cart">
                      Edit
                    </Link>
                    <div className="d-grid shadow-sm rounded">
                      <Button
                        type="button"
                        onClick={placeOrderHandler}
                        disabled={cart.cartItems.length === 0 || loading}
                        className="btn btn-primary px-4"
                        style={{ height: '45px', minWidth: '200px' }}
                      >
                        {loading ? (
                          <>
                            <Spinner
                              as="span"
                              animation="border"
                              size="sm"
                              role="status"
                              aria-hidden="true"
                              className="me-2"
                            />
                            Processing...
                          </>
                        ) : (
                          'Confirm & Send Order'
                        )}
                      </Button>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </div>
        </Container>
      </div>
    </div>
  );
}
